import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, ArrowRight, Check, Trophy, Users, User,
  AlertTriangle, Target, Shield, Zap, Star, Flag, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEAM_ISSUES = [
  { id: 'defensive_organization', label: 'Defensive Organization', icon: Shield },
  { id: 'passing_accuracy', label: 'Passing Accuracy', icon: Target },
  { id: 'decision_making', label: 'Decision Making', icon: Zap },
  { id: 'physical_intensity', label: 'Physical Intensity', icon: Star },
];

const TEAM_STRENGTHS = [
  { id: 'attack_fluidity', label: 'Attack Fluidity', icon: Zap },
  { id: 'defensive_solidity', label: 'Defensive Solidity', icon: Shield },
  { id: 'set_pieces', label: 'Set Pieces', icon: Target },
  { id: 'team_spirit', label: 'Team Spirit', icon: Users },
  { id: 'pressing', label: 'High Pressing', icon: Star },
];

const CONTRIBUTIONS = [
  { id: 'goal', label: 'Goal', icon: '\u26BD' },
  { id: 'assist', label: 'Assist', icon: '\uD83C\uDFAF' },
  { id: 'defensive_impact', label: 'Defense', icon: '\uD83D\uDEE1' },
  { id: 'effort', label: 'Effort', icon: '\uD83D\uDCAA' },
  { id: 'leadership', label: 'Leader', icon: '\uD83D\uDC51' },
];

const RATINGS = [
  { id: 'poor', label: 'Poor', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  { id: 'below_avg', label: 'Below', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  { id: 'average', label: 'Avg', color: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/30' },
  { id: 'good', label: 'Good', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  { id: 'excellent', label: 'Star', color: 'bg-primary/20 text-primary border-primary/30' },
];

const TeamMatchReport = () => {
  const { user, currentTeam } = useOutletContext();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [players, setPlayers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  const teamId = currentTeam?.team_id;
  
  const [matchData, setMatchData] = useState({
    team_id: teamId || '',
    opponent: '',
    our_score: 0,
    opponent_score: 0,
    result: ''
  });
  
  const [teamPerf, setTeamPerf] = useState({
    performance: 'average',
    issues: [],
    strengths: []
  });
  
  const [playerPerfs, setPlayerPerfs] = useState({});

  useEffect(() => {
    if (teamId) {
      setMatchData(prev => ({ ...prev, team_id: teamId }));
      fetchTeamPlayers();
    }
  }, [teamId]);

  useEffect(() => {
    const { our_score, opponent_score } = matchData;
    let result = '';
    if (our_score > opponent_score) result = 'win';
    else if (our_score < opponent_score) result = 'loss';
    else result = 'draw';
    setMatchData(prev => ({ ...prev, result }));
  }, [matchData.our_score, matchData.opponent_score]);

  const fetchTeamPlayers = async () => {
    const token = localStorage.getItem('team_token');
    try {
      const res = await fetch(`${API}/clubs/team/${teamId}/athletes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      const activePlayers = data || [];
      setPlayers(activePlayers);
      const defaults = {};
      activePlayers.forEach(p => {
        defaults[p.id] = {
          athlete_id: p.id,
          athlete_name: p.name || 'Unknown',
          rating: 'average',
          goals: 0,
          assists: 0,
          saves: 0,
          errors: 0,
          contributions: [],
          flag: 'no_issue',
          note: ''
        };
      });
      setPlayerPerfs(defaults);
    } catch (e) { console.error(e); }
  };

  const toggleIssue = (id) => {
    setTeamPerf(p => ({
      ...p,
      issues: p.issues.includes(id) ? p.issues.filter(x => x !== id) : [...p.issues, id]
    }));
  };

  const toggleStrength = (id) => {
    setTeamPerf(p => ({
      ...p,
      strengths: p.strengths.includes(id) ? p.strengths.filter(x => x !== id) : [...p.strengths, id]
    }));
  };

  const updatePlayerRating = (athleteId, rating) => {
    setPlayerPerfs(prev => ({
      ...prev,
      [athleteId]: { ...prev[athleteId], rating }
    }));
  };

  const updatePlayerGoals = (athleteId, delta) => {
    setPlayerPerfs(prev => ({
      ...prev,
      [athleteId]: { ...prev[athleteId], goals: Math.max(0, (prev[athleteId]?.goals || 0) + delta) }
    }));
  };

  const updatePlayerAssists = (athleteId, delta) => {
    setPlayerPerfs(prev => ({
      ...prev,
      [athleteId]: { ...prev[athleteId], assists: Math.max(0, (prev[athleteId]?.assists || 0) + delta) }
    }));
  };

  const updatePlayerSaves = (athleteId, delta) => {
    setPlayerPerfs(prev => ({
      ...prev,
      [athleteId]: { ...prev[athleteId], saves: Math.max(0, (prev[athleteId]?.saves || 0) + delta) }
    }));
  };

  const updatePlayerErrors = (athleteId, delta) => {
    setPlayerPerfs(prev => ({
      ...prev,
      [athleteId]: { ...prev[athleteId], errors: Math.max(0, (prev[athleteId]?.errors || 0) + delta) }
    }));
  };

  const toggleContribution = (athleteId, contrib) => {
    setPlayerPerfs(prev => {
      const current = prev[athleteId]?.contributions || [];
      return {
        ...prev,
        [athleteId]: {
          ...prev[athleteId],
          contributions: current.includes(contrib) ? current.filter(c => c !== contrib) : [...current, contrib]
        }
      };
    });
  };

  const toggleFlag = (athleteId, flag) => {
    setPlayerPerfs(prev => ({
      ...prev,
      [athleteId]: { ...prev[athleteId], flag: prev[athleteId]?.flag === flag ? 'no_issue' : flag }
    }));
  };

  const [summary, setSummary] = useState(null);

  const handleSubmit = async () => {
    setSubmitting(true);
    const token = localStorage.getItem('team_token');
    try {
      const res = await fetch(`${API}/clubs/matches/report`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          team_id: teamId,
          opponent: matchData.opponent,
          our_score: matchData.our_score,
          opponent_score: matchData.opponent_score,
          result: matchData.result,
          team_performance: teamPerf.performance,
          team_issues: teamPerf.issues,
          team_strong_points: teamPerf.strengths,
          player_performances: Object.values(playerPerfs)
        })
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      if (data.summary) {
        setSummary(data.summary);
        setStep(4);
      } else {
        toast.success('Match report submitted!');
        navigate(`/team/${teamId}`);
      }
    } catch (e) {
      toast.error('Failed to submit report');
    }
    setSubmitting(false);
  };

  const canAdvance = () => {
    if (step === 1) return matchData.opponent;
    return true;
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="team-match-report">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl uppercase">
            Quick <span className="text-primary">Match Report</span>
          </h1>
          <p className="text-muted-foreground text-sm">{currentTeam?.team_name} — Step {step} of 3</p>
        </div>
        <div className="flex gap-1">
          {[1, 2, 3, 4].map(s => (
            <div key={s} className={`w-10 h-1.5 rounded-full transition-colors ${s <= step ? (s === 4 ? 'bg-emerald-500' : 'bg-primary') : 'bg-zinc-700'}`} />
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            className="bg-card border border-border/40 rounded-sm p-6 space-y-5"
          >
            <h2 className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Trophy size={14} className="text-primary" /> Match Information
            </h2>
            
            <div className="p-3 bg-zinc-900/50 border border-border/20 rounded-sm">
              <p className="text-xs text-muted-foreground uppercase tracking-widest">Team</p>
              <p className="text-sm font-medium mt-1">{currentTeam?.team_name}</p>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Opponent</label>
              <input value={matchData.opponent} onChange={e => setMatchData(p => ({ ...p, opponent: e.target.value }))}
                placeholder="Opponent team name..."
                className="w-full bg-zinc-900 border border-border/40 rounded-sm px-3 py-2.5 text-sm outline-none focus:border-primary/50"
                data-testid="match-opponent-input"
              />
            </div>

            <div className="flex gap-6 items-end">
              <div className="flex-1">
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Our Score</label>
                <div className="flex items-center gap-3">
                  <button onClick={() => setMatchData(p => ({ ...p, our_score: Math.max(0, p.our_score - 1) }))}
                    className="w-10 h-10 bg-zinc-800 border border-border/30 rounded-sm text-lg hover:bg-zinc-700 transition-colors" data-testid="our-score-minus">-</button>
                  <span className="font-heading text-3xl text-primary w-10 text-center" data-testid="our-score">{matchData.our_score}</span>
                  <button onClick={() => setMatchData(p => ({ ...p, our_score: p.our_score + 1 }))}
                    className="w-10 h-10 bg-zinc-800 border border-border/30 rounded-sm text-lg hover:bg-zinc-700 transition-colors" data-testid="our-score-plus">+</button>
                </div>
              </div>
              <span className="font-heading text-2xl text-muted-foreground pb-1">{'\u00D7'}</span>
              <div className="flex-1">
                <label className="text-xs uppercase tracking-widest text-muted-foreground mb-2 block">Opponent Score</label>
                <div className="flex items-center gap-3">
                  <button onClick={() => setMatchData(p => ({ ...p, opponent_score: Math.max(0, p.opponent_score - 1) }))}
                    className="w-10 h-10 bg-zinc-800 border border-border/30 rounded-sm text-lg hover:bg-zinc-700 transition-colors" data-testid="opp-score-minus">-</button>
                  <span className="font-heading text-3xl w-10 text-center" data-testid="opponent-score">{matchData.opponent_score}</span>
                  <button onClick={() => setMatchData(p => ({ ...p, opponent_score: p.opponent_score + 1 }))}
                    className="w-10 h-10 bg-zinc-800 border border-border/30 rounded-sm text-lg hover:bg-zinc-700 transition-colors" data-testid="opp-score-plus">+</button>
                </div>
              </div>
            </div>

            {matchData.result && (
              <div className={`text-center py-2 px-4 rounded-sm text-sm uppercase tracking-wider font-heading ${
                matchData.result === 'win' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' :
                matchData.result === 'loss' ? 'bg-red-500/15 text-red-400 border border-red-500/20' :
                'bg-amber-500/15 text-amber-400 border border-amber-500/20'
              }`} data-testid="match-result">
                {matchData.result}
              </div>
            )}
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            className="bg-card border border-border/40 rounded-sm p-6 space-y-5"
          >
            <h2 className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <Users size={14} className="text-primary" /> Team Performance
            </h2>

            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground mb-3 block">Overall Performance</label>
              <div className="grid grid-cols-4 gap-2">
                {['poor', 'average', 'good', 'excellent'].map(p => (
                  <button key={p} onClick={() => setTeamPerf(prev => ({ ...prev, performance: p }))}
                    className={`py-3 rounded-sm text-sm uppercase tracking-wider border transition-all ${
                      teamPerf.performance === p
                        ? 'bg-primary/20 text-primary border-primary/40 font-medium'
                        : 'bg-zinc-900 text-muted-foreground border-border/30 hover:border-primary/20'
                    }`}
                    data-testid={`team-perf-${p}`}
                  >{p}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground mb-3 block">Issues Identified</label>
              <div className="grid grid-cols-2 gap-2">
                {TEAM_ISSUES.map(i => (
                  <button key={i.id} onClick={() => toggleIssue(i.id)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-sm text-sm border transition-all ${
                      teamPerf.issues.includes(i.id)
                        ? 'bg-red-500/10 text-red-400 border-red-500/30'
                        : 'bg-zinc-900 text-muted-foreground border-border/30 hover:border-red-500/20'
                    }`}
                  >
                    <i.icon size={14} /> {i.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground mb-3 block">Strong Points</label>
              <div className="grid grid-cols-2 gap-2">
                {TEAM_STRENGTHS.map(s => (
                  <button key={s.id} onClick={() => toggleStrength(s.id)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-sm text-sm border transition-all ${
                      teamPerf.strengths.includes(s.id)
                        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                        : 'bg-zinc-900 text-muted-foreground border-border/30 hover:border-emerald-500/20'
                    }`}
                  >
                    <s.icon size={14} /> {s.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            className="space-y-3"
          >
            <div className="bg-card border border-border/40 rounded-sm p-4">
              <h2 className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <User size={14} className="text-primary" /> Player Performance
                <span className="ml-auto text-[10px] text-zinc-500">{players.length} players</span>
              </h2>
              <p className="text-[11px] text-muted-foreground mt-1">Default: Average, No issues. Only change if needed.</p>
            </div>

            <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
              {players.map(p => {
                const perf = playerPerfs[p.id] || {};
                const hasChanges = perf.rating !== 'average' || perf.contributions?.length > 0 || perf.flag !== 'no_issue' || (perf.goals || 0) > 0 || (perf.assists || 0) > 0 || (perf.saves || 0) > 0 || (perf.errors || 0) > 0;
                return (
                  <div key={p.id}
                    className={`bg-card border rounded-sm p-3 transition-colors ${hasChanges ? 'border-primary/30' : 'border-border/30'}`}
                    data-testid={`player-perf-${p.id}`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center text-[10px] font-bold text-primary">
                        {(p.name || '?').charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{p.name}</p>
                        <p className="text-[10px] text-muted-foreground">{p.position || 'Player'}</p>
                      </div>
                    </div>

                    <div className="flex gap-1 mb-2">
                      {RATINGS.map(r => (
                        <button key={r.id} onClick={() => updatePlayerRating(p.id, r.id)}
                          className={`flex-1 py-1.5 text-[10px] uppercase tracking-wider rounded-sm border transition-all ${
                            perf.rating === r.id ? r.color + ' font-medium border' : 'bg-zinc-900/50 text-zinc-500 border-transparent hover:bg-zinc-800'
                          }`}
                        >{r.label}</button>
                      ))}
                    </div>

                    <div className="flex gap-3 mb-2 flex-wrap">
                      {/* Attack/Midfield: Goals & Assists */}
                      {(!p.position || !['Goalkeeper'].includes(p.position)) && (
                        <>
                          <div className="flex items-center gap-1.5 bg-zinc-900/50 rounded-sm px-2 py-1 border border-border/20">
                            <span className="text-[10px] text-muted-foreground">Goals</span>
                            <button onClick={() => updatePlayerGoals(p.id, -1)}
                              className="w-5 h-5 bg-zinc-800 rounded text-[10px] hover:bg-zinc-700 flex items-center justify-center">-</button>
                            <span className="text-sm font-heading text-primary w-4 text-center" data-testid={`goals-${p.id}`}>{perf.goals || 0}</span>
                            <button onClick={() => updatePlayerGoals(p.id, 1)}
                              className="w-5 h-5 bg-zinc-800 rounded text-[10px] hover:bg-zinc-700 flex items-center justify-center">+</button>
                          </div>
                          <div className="flex items-center gap-1.5 bg-zinc-900/50 rounded-sm px-2 py-1 border border-border/20">
                            <span className="text-[10px] text-muted-foreground">Assists</span>
                            <button onClick={() => updatePlayerAssists(p.id, -1)}
                              className="w-5 h-5 bg-zinc-800 rounded text-[10px] hover:bg-zinc-700 flex items-center justify-center">-</button>
                            <span className="text-sm font-heading text-primary w-4 text-center" data-testid={`assists-${p.id}`}>{perf.assists || 0}</span>
                            <button onClick={() => updatePlayerAssists(p.id, 1)}
                              className="w-5 h-5 bg-zinc-800 rounded text-[10px] hover:bg-zinc-700 flex items-center justify-center">+</button>
                          </div>
                        </>
                      )}
                      {/* Goalkeeper: Saves & Errors */}
                      {p.position === 'Goalkeeper' && (
                        <>
                          <div className="flex items-center gap-1.5 bg-zinc-900/50 rounded-sm px-2 py-1 border border-blue-500/20">
                            <span className="text-[10px] text-blue-400">Saves</span>
                            <button onClick={() => updatePlayerSaves(p.id, -1)}
                              className="w-5 h-5 bg-zinc-800 rounded text-[10px] hover:bg-zinc-700 flex items-center justify-center">-</button>
                            <span className="text-sm font-heading text-blue-400 w-4 text-center">{perf.saves || 0}</span>
                            <button onClick={() => updatePlayerSaves(p.id, 1)}
                              className="w-5 h-5 bg-zinc-800 rounded text-[10px] hover:bg-zinc-700 flex items-center justify-center">+</button>
                          </div>
                          <div className="flex items-center gap-1.5 bg-zinc-900/50 rounded-sm px-2 py-1 border border-red-500/20">
                            <span className="text-[10px] text-red-400">Errors</span>
                            <button onClick={() => updatePlayerErrors(p.id, -1)}
                              className="w-5 h-5 bg-zinc-800 rounded text-[10px] hover:bg-zinc-700 flex items-center justify-center">-</button>
                            <span className="text-sm font-heading text-red-400 w-4 text-center">{perf.errors || 0}</span>
                            <button onClick={() => updatePlayerErrors(p.id, 1)}
                              className="w-5 h-5 bg-zinc-800 rounded text-[10px] hover:bg-zinc-700 flex items-center justify-center">+</button>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="flex gap-1 mb-1.5">
                      {CONTRIBUTIONS.map(c => (
                        <button key={c.id} onClick={() => toggleContribution(p.id, c.id)}
                          className={`flex-1 py-1 text-[9px] rounded-sm border transition-all ${
                            perf.contributions?.includes(c.id)
                              ? 'bg-primary/15 text-primary border-primary/30'
                              : 'bg-zinc-900/30 text-zinc-600 border-transparent hover:bg-zinc-800/50'
                          }`}
                        >{c.icon} {c.label}</button>
                      ))}
                    </div>

                    <div className="flex gap-1">
                      <button onClick={() => toggleFlag(p.id, 'needs_development')}
                        className={`flex-1 flex items-center justify-center gap-1 py-1 text-[9px] rounded-sm border transition-all ${
                          perf.flag === 'needs_development'
                            ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                            : 'bg-zinc-900/30 text-zinc-600 border-transparent hover:bg-zinc-800/50'
                        }`}
                      ><AlertTriangle size={10} /> Needs Dev</button>
                      <button onClick={() => toggleFlag(p.id, 'performance_drop')}
                        className={`flex-1 flex items-center justify-center gap-1 py-1 text-[9px] rounded-sm border transition-all ${
                          perf.flag === 'performance_drop'
                            ? 'bg-red-500/15 text-red-400 border-red-500/30'
                            : 'bg-zinc-900/30 text-zinc-600 border-transparent hover:bg-zinc-800/50'
                        }`}
                      ><Flag size={10} /> Perf Drop</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
        {step === 4 && summary && (
          <motion.div key="step4" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-emerald-500/30 rounded-sm p-6 space-y-5"
          >
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3">
                <Check size={24} className="text-emerald-400" />
              </div>
              <h2 className="font-heading text-xl uppercase">Post-Match <span className="text-emerald-400">Summary</span></h2>
              <p className="text-sm text-muted-foreground mt-1">{summary.match_result} vs {matchData.opponent}</p>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-zinc-900/50 rounded-sm border border-border/20">
                <p className="font-heading text-xl text-primary">{summary.avg_player_score}</p>
                <p className="text-[10px] text-muted-foreground uppercase">Avg Score</p>
              </div>
              <div className="text-center p-3 bg-zinc-900/50 rounded-sm border border-border/20">
                <p className={`font-heading text-xl ${summary.comparison?.score_trend === 'improving' ? 'text-emerald-400' : summary.comparison?.score_trend === 'declining' ? 'text-red-400' : 'text-amber-400'}`}>
                  {summary.comparison?.score_trend?.toUpperCase()}
                </p>
                <p className="text-[10px] text-muted-foreground uppercase">Trend</p>
              </div>
              <div className="text-center p-3 bg-zinc-900/50 rounded-sm border border-border/20">
                <p className="font-heading text-xl text-primary">{summary.comparison?.win_rate}%</p>
                <p className="text-[10px] text-muted-foreground uppercase">Win Rate</p>
              </div>
            </div>

            {summary.scorers?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Goal Scorers</p>
                <div className="flex flex-wrap gap-2">
                  {summary.scorers.map((s, i) => (
                    <span key={i} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-sm border border-primary/20">
                      {s.name} ({s.goals}G{s.assists > 0 ? ` ${s.assists}A` : ''})
                    </span>
                  ))}
                </div>
              </div>
            )}

            {summary.standout_players?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Standout Players</p>
                <div className="flex flex-wrap gap-2">
                  {summary.standout_players.map((s, i) => (
                    <span key={i} className="px-2 py-1 bg-emerald-500/10 text-emerald-400 text-xs rounded-sm border border-emerald-500/20">
                      {s.name} ({s.rating})
                    </span>
                  ))}
                </div>
              </div>
            )}

            {summary.concerns?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Needs Attention</p>
                <div className="flex flex-wrap gap-2">
                  {summary.concerns.map((c, i) => (
                    <span key={i} className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded-sm border border-red-500/20">
                      {c.name} — {c.flag.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {summary.insights?.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Insights</p>
                {summary.insights.map((ins, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 bg-zinc-900/40 rounded-sm border border-border/10">
                    <Zap size={12} className="text-primary mt-0.5 shrink-0" />
                    <p className="text-sm text-muted-foreground">{ins}</p>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex items-center justify-between mt-6">
        {step === 4 ? (
          <div className="w-full flex justify-center">
            <Button onClick={() => navigate(`/team/${teamId}`)}
              className="bg-primary text-black hover:bg-primary/90"
            >
              <Check size={16} className="mr-2" /> Done
            </Button>
          </div>
        ) : (
          <>
            <Button variant="outline" onClick={() => step === 1 ? navigate(`/team/${teamId}`) : setStep(s => s - 1)}
              className="border-border/40"
            >
              <ArrowLeft size={16} className="mr-2" /> {step === 1 ? 'Cancel' : 'Back'}
            </Button>
            
            {step < 3 ? (
              <Button onClick={() => setStep(s => s + 1)} disabled={!canAdvance()}
                className="bg-primary text-black hover:bg-primary/90"
                data-testid="match-next-btn"
              >
                Next <ArrowRight size={16} className="ml-2" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={submitting}
                className="bg-primary text-black hover:bg-primary/90"
                data-testid="match-submit-btn"
              >
                {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Check size={16} className="mr-2" />}
                Submit Report
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TeamMatchReport;
